import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  Typography,
  Grid,
  Box,
  Alert,
  Collapse,
  Container,
  FormControlLabel,
} from "@mui/material";
import api from "../../globals/axios";
import { useTranslation } from "react-i18next";
import CropHealth from "../../assets/Icon/CropHealth.png";
import GenericButton from "../../components/Custom/GenericButton";
import CropSelectionPopUp from "../../components/Advisory/CropSelectionPopUp";
import CountdownLoader from "../../components/CountdownLoader";
import RemoveImageButton from "../../components/RemoveImageButton";
import { isNotNullOrEmptyArray, compressImage } from "../../globals/utils";
import { MAX_FILE_SIZE } from "../../globals/const";
import CropSelectionDropdown from "../../components/Advisory/CropSelectionDropdown";
import {
  StyledTogglePaper,
  StyledSwitch,
  containerStyles,
  mainBoxStyles,
  formContainerStyles,
  toggleRowStyles,
  toggleTextStyles,
  imageContainerStyles,
  uploadedImageContainerStyles,
  uploadedImageStyles,
  uploadInstructionsStyles,
  uploadButtonContainerStyles,
  fileInputButtonStyles,
  cameraButtonStyles,
  processButtonContainerStyles,
  errorContainerStyles,
  alertStyles,
  loaderContainerStyles,
  resultsContainerStyles,
  resultsBoxStyles,
  resultsTitleStyles,
  resultCardStyles,
  cardTitleStyles,
  infoRowStyles,
  infoIconContainerStyles,
  infoTextContainerStyles,
  infoLabelStyles,
  infoValueStyles,
  getSeverityColor,
  getSeverityTextColor,
  descriptionStyles,
  listContainerStyles,
  listItemStyles,
  stepContainerStyles,
  stepRowStyles,
  stepNumberStyles,
  bulletPointStyles,
  stepTextStyles,
  confidenceLevelContainerStyles,
  getConfidenceLevelColor,
  actionButtonContainerStyles,
} from "../../css/PlantDiseasePredication.styles";
import {
  INITIAL_ANALYSIS_STATE,
  extractAnalysisData,
  updateStateWithData,
} from "../../utils/analysisUtils";

// Group constants at the top of the file

const PlantDiseasePrediction = () => {
  // Image related state
  const [imageFile, setImageFile] = useState(null);
  const [imageUploadFile, setImageUploadFile] = useState(null);
  const imageFileUrlRef = useRef(null);

  // Analysis result state
  const [diseaseName, setDiseaseName] = useState("");
  const [description, setDescription] = useState("");
  const [plantName, setPlantName] = useState("");
  const [diseaseSeverity, setDiseaseSeverity] = useState("");
  const [recommendations, setRecommendations] = useState([]);
  const [nextSteps, setNextSteps] = useState([]);
  const [nutritionalDeficiencies, setNutritionalDeficiencies] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [confidenceLevel, setConfidenceLevel] = useState("");
  const [results, setResults] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [, setTaskId] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [isCountdownComplete, setIsCountdownComplete] = useState(false);

  // Farmland and crop selection state
  const [hasFarmland, setHasFarmland] = useState(false);
  const [cropOptions, setCropOptions] = useState([]);
  const [selectedPlantId, setSelectedPlantId] = useState("");
  const [selectedPlantName, setSelectedPlantName] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Add cached crop data state
  const [farmlandCrops, setFarmlandCrops] = useState([]);
  const [generalCrops, setGeneralCrops] = useState([]);
  const [hasFetchedFarmlandCrops, setHasFetchedFarmlandCrops] = useState(false);
  const [hasFetchedGeneralCrops, setHasFetchedGeneralCrops] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({ show: false, message: "" });
  const [showCropPopup, setShowCropPopup] = useState(false);
  const [predictionId, setPredictionId] = useState(null);

  // Refs
  const fileInputRef = useRef(null);
  const processButtonRef = useRef(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // On initial load, fetch general crops
    fetchCommodities();
  }, []);

  useEffect(() => {
    return () => {
      if (imageFileUrlRef.current) {
        URL.revokeObjectURL(imageFileUrlRef.current);
      }
    };
  }, []);

  // Replace the existing useEffect for hasFarmland
  useEffect(() => {
    if (hasFarmland) {
      // Only fetch farmland crops if we haven't fetched them before
      if (!hasFetchedFarmlandCrops) {
        fetchFarmlandCrops();
      } else {
        // Use cached farmland crops
        setCropOptions(farmlandCrops);
        setLoadingOptions(false);
      }
    } else {
      // Use cached general crops
      setCropOptions(generalCrops);
      setLoadingOptions(false);
    }

    // Reset plant selection when toggling
    setSelectedPlantId("");
    setSelectedPlantName("");
  }, [hasFarmland]);

  // Function to fetch crops from farmland
  const fetchFarmlandCrops = async () => {
    setLoadingOptions(true);
    setCropOptions([]);

    try {
      const response = await api.get("/farmer/crops/");
      if (response.data && response.data.length > 0) {
        // Extract unique crop names from farmland crops
        const crops = response.data.map((crop) => ({
          id: crop.id,
          name: crop.commodity ? crop.commodity.name : "Unknown Crop",
        }));

        // Update both state variables
        setFarmlandCrops(crops);
        setCropOptions(crops);
        setHasFetchedFarmlandCrops(true);
      } else {
        setFarmlandCrops([]);
        setCropOptions([]);
        setError({
          show: true,
          message:
            "No crops found in your farmlands. Switching to general crops.",
        });
        setHasFarmland(false);
      }
    } catch (error) {
      console.error("Error fetching farmland crops:", error);
      setError({
        show: true,
        message:
          "Failed to load your farmland crops. Switching to general crops.",
      });
      setHasFarmland(false);
    } finally {
      setLoadingOptions(false);
    }
  };

  // Function to fetch all available commodities (crops)
  const fetchCommodities = async () => {
    setLoadingOptions(true);
    setCropOptions([]);

    // Skip API call if we already have the data
    if (hasFetchedGeneralCrops) {
      setCropOptions(generalCrops);
      setLoadingOptions(false);
      return;
    }

    try {
      const response = await api.get("/common/commodities/");
      if (response.data && response.data.length > 0) {
        // Filter to only include CROP type
        const crops = response.data
          .filter((commodity) => commodity.type === "CROP")
          .map((commodity) => ({
            id: commodity.id,
            name: commodity.name,
          }));

        // Update both state variables
        setGeneralCrops(crops);
        setCropOptions(crops);
        setHasFetchedGeneralCrops(true);
      } else {
        setGeneralCrops([]);
        setCropOptions([]);
      }
    } catch (error) {
      console.error("Error fetching commodities:", error);
      setError({
        show: true,
        message: "Failed to load crop options.",
      });
    } finally {
      setLoadingOptions(false);
    }
  };

  // Consolidated reset function
  const resetAnalysis = () => {
    // Create setter functions mapping
    const setterFunctions = {
      diseaseName: setDiseaseName,
      description: setDescription,
      plantName: setPlantName,
      diseaseSeverity: setDiseaseSeverity,
      recommendations: setRecommendations,
      nextSteps: setNextSteps,
      nutritionalDeficiencies: setNutritionalDeficiencies,
      symptoms: setSymptoms,
      confidenceLevel: setConfidenceLevel,
    };

    // Use utility function to reset all state values
    updateStateWithData(INITIAL_ANALYSIS_STATE, setterFunctions);

    // Reset results state to prevent old data from persisting
    setResults({});
    setPredictionId(null);
    setShowResults(false);
    setSelectedPlantId("");
    setSelectedPlantName("");

    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setTaskId(null);
  };

  const handleTestAnother = () => {
    resetAnalysis();
    handleRemoveImage();
  };

  const handleUpload = async () => {
    if (!imageUploadFile) {
      setError({ show: true, message: "Please select an image to analyze" });
      return;
    }

    if (!selectedPlantId || !selectedPlantName) {
      setError({ show: true, message: "Please select a plant type first" });
      return;
    }

    resetAnalysis();
    setError({ show: false, message: "" });
    setLoading(true);

    const formData = new FormData();
    formData.append("image", imageUploadFile);
    formData.append("plant_name", selectedPlantName); // Send plant name for analysis
    formData.append("language", i18n.language || "english"); // Add language parameter

    try {
      const response = await api.post("/ai/predict/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.status === "SUCCESS") {
        setTaskId(response.data.data.task_id);
        startPolling(response.data.data.task_id);
      } else {
        setError({
          show: true,
          message:
            response.data.message || "Error analyzing image. Please try again.",
        });
        setLoading(false);
      }
    } catch (error) {
      handlePredictionError(error);
    }
  };

  const handlePredictionError = (error) => {
    console.error("Error in prediction:", error);
    let errorMessage =
      "Image is not clear enough. Please try again with a clearer image.";

    // Reset states
    setError({ show: true, message: errorMessage });
    setResults({});
    setLoading(false);

    // Clear polling if active
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }

    // Reset task ID
    setTaskId(null);
  };

  // useEffect for cleanup
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, []);

  const startPolling = (taskId) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/ai/predict/?task_id=${taskId}`);
        if (
          response.data.status === "SUCCESS" &&
          response.data.data.status === "SUCCESS"
        ) {
          clearInterval(interval);
          setPollingInterval(null);
          handlePredictionSuccess(response.data.data);
        }
      } catch (error) {
        clearInterval(interval);
        setPollingInterval(null);
        handlePredictionError(error);
      }
    }, 5000);

    setPollingInterval(interval);
  };

  const handlePredictionSuccess = (data) => {
    const resultData = data.result;
    setResults(resultData);
    setPredictionId(data.prediction_id);

    try {
      api.patch(`/ai/update-ai-prediction-history-crop/`, {
        prediction_id: data.prediction_id,
        crop_id: selectedPlantId,
      });
    } catch (error) {
      console.error("Error updating prediction with crop:", error);
    }

    handleAnalysisResults();
    setShowResults(true);
    setLoading(false);
  };

  // Improved analysis handler with utility functions
  const handleAnalysisResults = () => {
    // Extract and format data from API response
    const extractedData = extractAnalysisData(results);
    if (!extractedData) return;

    // Set state with extracted data
    const setterFunctions = {
      diseaseName: setDiseaseName,
      description: setDescription,
      plantName: setPlantName,
      diseaseSeverity: setDiseaseSeverity,
      recommendations: setRecommendations,
      nextSteps: setNextSteps,
      nutritionalDeficiencies: setNutritionalDeficiencies,
      symptoms: setSymptoms,
      confidenceLevel: setConfidenceLevel,
    };

    // Update all state variables at once
    updateStateWithData(extractedData, setterFunctions);

    // Always show results if we have any data
    setShowResults(true);
  };

  useEffect(() => {
    handleAnalysisResults();
  }, [results, i18n.language]);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setError({ show: false, message: "" });

      // Check file size but don't show error message - just log it
      if (file.size > MAX_FILE_SIZE) {
        console.log(
          `Large file detected: ${(file.size / (1024 * 1024)).toFixed(
            2
          )}MB. Will attempt compression.`
        );
      }

      try {
        const originalSizeKB = (file.size / 1024).toFixed(2);
        const originalSizeMB = (file.size / (1024 * 1024)).toFixed(2);

        // Compress the image before uploading
        const compressedFile = await compressImage(file);

        const compressedSizeKB = (compressedFile.size / 1024).toFixed(2);
        const compressedSizeMB = (compressedFile.size / (1024 * 1024)).toFixed(
          2
        );
        const compressionRatio = (
          (1 - compressedFile.size / file.size) *
          100
        ).toFixed(2);

        console.log(
          `Image compression details:
          - Original: ${originalSizeKB}KB (${originalSizeMB}MB)
          - Compressed: ${compressedSizeKB}KB (${compressedSizeMB}MB)
          - Reduction: ${compressionRatio}%`
        );

        // Check if compressed file is still too large
        if (compressedFile.size > MAX_FILE_SIZE) {
          setImageFile(null);
          setImageUploadFile(null);
          setError({
            show: true,
            message: `Even after compression, file size is too large (${(
              compressedFile.size /
              (1024 * 1024)
            ).toFixed(2)}MB). Please select a smaller image.`,
          });
          return;
        }

        setImageUploadFile(compressedFile);

        // Revoke the previous object URL if it exists
        if (imageFileUrlRef.current) {
          URL.revokeObjectURL(imageFileUrlRef.current);
        }
        const newObjectUrl = URL.createObjectURL(compressedFile);
        imageFileUrlRef.current = newObjectUrl; // Store the new object URL
        setImageFile(newObjectUrl);

        setTimeout(() => {
          if (processButtonRef.current) {
            processButtonRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      } catch (error) {
        console.error("Error compressing image:", error);
        setError({
          show: true,
          message: "Failed to process the image. Please try another image.",
        });
      }
    }
  };

  const handlePopupClose = () => {
    setShowCropPopup(false);
    // Since we're no longer using the popup for selection, process results directly
    handleAnalysisResults();
  };

  const handleRemoveImage = () => {
    if (imageFileUrlRef.current) {
      URL.revokeObjectURL(imageFileUrlRef.current);
      imageFileUrlRef.current = null;
    }
    setImageFile(null);
    setImageUploadFile(null);
    setError({ show: false, message: "" });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFarmlandToggle = (event) => {
    setHasFarmland(event.target.checked);
  };

  const handlePlantSelection = (event) => {
    // Find the selected crop from options
    const selectedCropId = event.target.value;
    const selectedCrop = cropOptions.find(
      (crop) => crop.id.toString() === selectedCropId.toString()
    );

    if (selectedCrop) {
      setSelectedPlantId(selectedCrop.id);
      setSelectedPlantName(selectedCrop.name);
    } else {
      setSelectedPlantId("");
      setSelectedPlantName("");
    }
  };

  return (
    <Box>
      <Helmet>
        <title>Plant Disease Prediction - Farmapp</title>
      </Helmet>
      <Container sx={containerStyles}>
        <Box sx={mainBoxStyles}>
          {!showResults ? (
            <Box sx={formContainerStyles}>
              <StyledTogglePaper elevation={0}>
                <Box sx={toggleRowStyles}>
                  <Typography
                    variant="body1"
                    fontWeight="600"
                    sx={toggleTextStyles}
                  >
                    {t("Do you have a farmland?")}
                  </Typography>
                  <FormControlLabel
                    control={
                      <StyledSwitch
                        checked={hasFarmland}
                        onChange={handleFarmlandToggle}
                      />
                    }
                    label={hasFarmland ? t("Yes") : t("No")}
                  />
                </Box>

                <CropSelectionDropdown
                  selectedPlantId={selectedPlantId}
                  selectedPlantName={selectedPlantName}
                  handlePlantSelection={handlePlantSelection}
                  loadingOptions={loadingOptions}
                  cropOptions={cropOptions}
                />
              </StyledTogglePaper>

              <Box
                sx={{
                  ...imageContainerStyles,
                  backgroundColor: imageFile ? "transparent" : "white", // Only conditional style
                }}
              >
                {imageFile ? (
                  <Box sx={uploadedImageContainerStyles}>
                    <img
                      src={imageFile}
                      alt="Selected"
                      style={uploadedImageStyles}
                    />

                    <RemoveImageButton onClick={handleRemoveImage} />
                  </Box>
                ) : (
                  <>
                    <img
                      src={CropHealth}
                      alt="Upload"
                      style={{
                        width: "120px",
                      }}
                    />
                    <Typography
                      variant="h6"
                      mt={2}
                      sx={{ color: "text.primary" }}
                    >
                      {t("Upload a Crop photo")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={uploadInstructionsStyles}
                    >
                      {t(
                        "You can upload a photo by importing or clicking with your camera"
                      )}
                    </Typography>
                  </>
                )}
                <div style={uploadButtonContainerStyles}>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="select-image"
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                  />
                  <input
                    accept="image/*"
                    capture="environment"
                    style={{ display: "none" }}
                    id="capture-image"
                    type="file"
                    onChange={handleImageUpload}
                  />
                  {!loading && (
                    <>
                      <label
                        htmlFor="select-image"
                        style={fileInputButtonStyles}
                      >
                        <span style={{ marginRight: "5px" }}>📁</span>
                        {imageFile ? t("Change Image") : t("Select Image")}
                      </label>
                      <label htmlFor="capture-image" style={cameraButtonStyles}>
                        <span style={{ marginRight: "5px" }}>📷</span>
                        {t("Take Photo")}
                      </label>
                    </>
                  )}
                </div>
              </Box>

              {imageFile && !loading && (
                <Box sx={processButtonContainerStyles} ref={processButtonRef}>
                  <GenericButton
                    padding="10px"
                    backgroundColor="#4CAF50"
                    fontColor="#fff"
                    onClick={handleUpload}
                    disabled={!imageFile || loading || !selectedPlantId}
                  >
                    {loading ? t("Processing...") : t("Process")}
                  </GenericButton>
                </Box>
              )}

              <Box sx={errorContainerStyles}>
                <Collapse in={error.show}>
                  <Alert
                    severity="error"
                    sx={alertStyles}
                    onClose={() => setError({ ...error, show: false })}
                  >
                    {error.message}
                  </Alert>
                </Collapse>
              </Box>
            </Box>
          ) : null}
          {loading ? (
            <Box sx={loaderContainerStyles}>
              <Grid container justifyContent="center" alignItems="center">
                <Grid
                  item
                  xs={12}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  {!isCountdownComplete && (
                    <CountdownLoader
                      initialSeconds={30}
                      onComplete={() => {
                        setIsCountdownComplete(true);
                        if (loading) {
                          setError({
                            show: false,
                            message:
                              "Analysis is taking longer than expected. Please wait while we process your request...",
                          });
                        }
                      }}
                    />
                  )}
                  {isCountdownComplete && (
                    <Box
                      sx={{ backgroundColor: "#fff", p: 2, borderRadius: 2 }}
                    >
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ textAlign: "center", mt: 2 }}
                      >
                        {t(
                          "Analysis is taking longer than expected. Please wait while we process your request..."
                        )}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          ) : (
            showResults && (
              <Box sx={resultsContainerStyles}>
                {" "}
                <Box sx={resultsBoxStyles}>
                  <Typography variant="h5" sx={resultsTitleStyles}>
                    {t("Analysis Results")}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={resultCardStyles}>
                        <Typography variant="h6" sx={cardTitleStyles}>
                          {t("Plant Information")}
                        </Typography>

                        <Box sx={infoRowStyles}>
                          <Box sx={infoIconContainerStyles("#e8f5e9")}>
                            <span style={{ fontSize: "18px" }}>🌱</span>
                          </Box>
                          <Box sx={infoTextContainerStyles}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={infoLabelStyles}
                            >
                              {t("Plant Name")}
                            </Typography>
                            <Typography
                              variant="body1"
                              fontWeight="medium"
                              sx={infoValueStyles}
                            >
                              {plantName}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={infoRowStyles}>
                          <Box sx={infoIconContainerStyles("#ffebee")}>
                            <span style={{ fontSize: "18px" }}>🦠</span>
                          </Box>
                          <Box sx={infoTextContainerStyles}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={infoLabelStyles}
                            >
                              {t("Disease Name")}
                            </Typography>
                            <Typography
                              variant="body1"
                              fontWeight="medium"
                              sx={infoValueStyles}
                            >
                              {diseaseName &&
                              diseaseName !== "None" &&
                              diseaseName !== "N/A"
                                ? diseaseName
                                : t("No disease detected")}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={infoRowStyles}>
                          <Box
                            sx={infoIconContainerStyles(
                              getSeverityColor(diseaseSeverity)
                            )}
                          >
                            <span style={{ fontSize: "18px" }}>⚠️</span>
                          </Box>
                          <Box sx={infoTextContainerStyles}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={infoLabelStyles}
                            >
                              {t("Disease Severity")}
                            </Typography>
                            <Typography
                              variant="body1"
                              fontWeight="medium"
                              sx={{
                                ...infoValueStyles,
                                color: getSeverityTextColor(diseaseSeverity),
                              }}
                            >
                              {diseaseSeverity}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box sx={resultCardStyles}>
                        <Typography variant="h6" sx={cardTitleStyles}>
                          {t("Analysis")}
                        </Typography>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={descriptionStyles}
                        >
                          {description}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box sx={resultCardStyles}>
                        <Typography variant="h6" sx={cardTitleStyles}>
                          {t("Recommendations")}
                        </Typography>
                        {recommendations.length > 0 ? (
                          <Box component="ul" sx={listContainerStyles}>
                            {recommendations.map((recommendation, index) => (
                              <Box
                                component="li"
                                key={index}
                                sx={listItemStyles}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{ overflowWrap: "break-word" }}
                                >
                                  {recommendation}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body1" color="text.secondary">
                            {t("No recommendations available")}
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box sx={resultCardStyles}>
                        <Typography variant="h6" sx={cardTitleStyles}>
                          {t("Next Steps")}
                        </Typography>
                        {isNotNullOrEmptyArray(nextSteps) ? (
                          <Box sx={stepContainerStyles}>
                            {nextSteps.map((step, index) => (
                              <Box key={index} sx={stepRowStyles}>
                                <Box sx={stepNumberStyles()}>{index + 1}</Box>
                                <Typography
                                  variant="body1"
                                  color="text.secondary"
                                  sx={stepTextStyles}
                                >
                                  {step}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body1" color="text.secondary">
                            {t("No next steps available")}
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {isNotNullOrEmptyArray(symptoms) && (
                      <Grid item xs={12} md={6}>
                        <Box sx={resultCardStyles}>
                          <Typography variant="h6" sx={cardTitleStyles}>
                            {t("Symptoms")}
                          </Typography>
                          <Box component="ul" sx={listContainerStyles}>
                            {symptoms.map((symptom, index) => (
                              <Box
                                component="li"
                                key={index}
                                sx={{
                                  ...listItemStyles,
                                  "&::marker": { color: "#ff9800" },
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{ overflowWrap: "break-word" }}
                                >
                                  {symptom}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <Box sx={resultCardStyles}>
                        <Typography variant="h6" sx={cardTitleStyles}>
                          {t("Nutritional Deficiencies")}
                        </Typography>
                        {isNotNullOrEmptyArray(nutritionalDeficiencies) ? (
                          <Box sx={stepContainerStyles}>
                            {nutritionalDeficiencies.map(
                              (deficiency, index) => (
                                <Box key={index} sx={stepRowStyles}>
                                  <Box sx={bulletPointStyles}>•</Box>
                                  <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={stepTextStyles}
                                  >
                                    {deficiency}
                                  </Typography>
                                </Box>
                              )
                            )}
                          </Box>
                        ) : (
                          <Box sx={stepRowStyles}>
                            <Box
                              sx={bulletPointStyles}
                              style={{ color: "#ff9800" }}
                            >
                              •
                            </Box>
                            <Typography
                              variant="body1"
                              color="text.secondary"
                              sx={stepTextStyles}
                            >
                              {t("No nutritional deficiencies detected")}
                            </Typography>
                          </Box>
                        )}

                        {confidenceLevel && (
                          <Box sx={confidenceLevelContainerStyles}>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              mr={1}
                            >
                              {t("Confidence Level")}:
                            </Typography>
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              sx={{
                                color: getConfidenceLevelColor(confidenceLevel),
                              }}
                            >
                              {confidenceLevel}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Test Another Crop button */}
                  <Box sx={actionButtonContainerStyles}>
                    <GenericButton
                      padding="10px"
                      backgroundColor="#4CAF50"
                      fontColor="#fff"
                      onClick={handleTestAnother}
                    >
                      {t("Test Another Crop")}
                    </GenericButton>
                  </Box>
                </Box>
              </Box>
            )
          )}
          {/* Popup component */}
          {showCropPopup && (
            <CropSelectionPopUp
              open={showCropPopup}
              onClose={handlePopupClose}
              prediction_id={predictionId}
            />
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default PlantDiseasePrediction;
